// Limit large app-served previews, including requests from both builder views.
const MAX_ACTIVE_PREVIEWS = 4
export const APP_PREVIEW_TIMEOUT_MS = 15000
let active = 0
const waiting = []

function acquire(signal) {
  return new Promise((resolve, reject) => {
    const cancel = () => {
      const index = waiting.indexOf(start)
      if (index >= 0) waiting.splice(index, 1)
      reject(new DOMException('Artwork loading cancelled', 'AbortError'))
    }
    const start = () => {
      signal?.removeEventListener('abort', cancel)
      if (signal?.aborted) return cancel()
      active += 1
      resolve()
    }
    if (signal?.aborted) return cancel()
    if (active < MAX_ACTIVE_PREVIEWS) start()
    else {
      waiting.push(start)
      signal?.addEventListener('abort', cancel, { once: true })
    }
  })
}

export async function fetchArtworkPreview(url, options) {
  const controller = new AbortController()
  const cancel = () => controller.abort()
  if (options.signal?.aborted) cancel()
  else options.signal?.addEventListener('abort', cancel, { once: true })
  const timer = setTimeout(cancel, APP_PREVIEW_TIMEOUT_MS)
  let acquired = false
  let cancelRequest
  try {
    await acquire(controller.signal)
    acquired = true
    const aborted = new Promise((resolve, reject) => {
      cancelRequest = () => reject(new DOMException('Artwork loading cancelled', 'AbortError'))
      if (controller.signal.aborted) cancelRequest()
      else controller.signal.addEventListener('abort', cancelRequest, { once: true })
    })
    const request = (async () => {
      const response = await fetch(url, { ...options, signal: controller.signal })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Could not load artwork preview')
      }
      const blob = await response.blob()
      if (controller.signal.aborted) throw new DOMException('Artwork loading cancelled', 'AbortError')
      return URL.createObjectURL(blob)
    })()
    return await Promise.race([request, aborted])
  } finally {
    clearTimeout(timer)
    options.signal?.removeEventListener('abort', cancel)
    if (cancelRequest) controller.signal.removeEventListener('abort', cancelRequest)
    if (acquired) {
      active -= 1
      waiting.shift()?.()
    }
  }
}
